/**
 * Shared admin Time filter presets (Figma Time filter dropdown).
 * `days: null` + no bounds = all-time.
 * Financial Year presets (`fy-0`…`fy-3`) are Finance-only: always 1 Jul → 30 Jun.
 * Custom range uses encoded values `custom|YYYY-MM-DD|YYYY-MM-DD` (Melbourne calendar days,
 * inclusive). Query window is half-open [start midnight, day-after-end midnight).
 *
 * Trailing presets (TM-3568):
 * - Last 24 Hours (`days: 1`) → API rolling datetime window.
 * - Last 7/30/… → Melbourne calendar-day absolute Instants (date-only), same zone as Custom/FY.
 */
import { differenceInCalendarDays, format, isValid, parseISO, subDays } from 'date-fns';

export type AdminTimeRangeDayValue =
  | 'all'
  | '1'
  | '7'
  | '30'
  | '60'
  | '90'
  | '180'
  | '365'
  | 'custom';

/** Current FY = fy-0; prior years = fy-1 … fy-3. Finance Management only. */
export type AdminFinancialYearValue = `fy-${0 | 1 | 2 | 3}`;

export type AdminTimeRangeValue = AdminTimeRangeDayValue | AdminFinancialYearValue;

/** Menu value for the Custom range row; applied selections use {@link encodeCustomTimeRange}. */
export const CUSTOM_TIME_RANGE_VALUE = 'custom' as const;

export type AdminTimeRangeOption = {
  value: AdminTimeRangeValue;
  label: string;
  /** Trailing-day presets only; FY / custom rows use absolute bounds instead. */
  days: number | null;
};

export type CustomTimeRangeParts = {
  startYmd: string;
  endYmdInclusive: string;
};

/** Closed-state / page-load Time filter selection (Figma default). */
export const DEFAULT_ADMIN_TIME_RANGE: AdminTimeRangeValue = '30';
export const DEFAULT_ADMIN_TIME_RANGE_DAYS = 30;

/** How many completed prior AU FYs to offer alongside the current year (Finance only). */
export const FINANCIAL_YEAR_HISTORY = 3;

/** AU financial-year calendar for TipMi (not UTC). */
export const AU_FY_TIME_ZONE = 'Australia/Melbourne';

/** Figma subtitle under the Time filter trigger, e.g. "Jul 01, 2025 – Jun 30, 2026". */
const TIME_RANGE_DAY_FMT = 'MMM dd, yyyy';

export type AustralianFinancialYear = {
  offset: number;
  startYear: number;
  endYear: number;
  label: string;
  value: AdminFinancialYearValue;
  /** Inclusive calendar start: 1 July startYear (Melbourne midnight → UTC Instant). */
  start: Date;
  /**
   * Exclusive query end: 1 July endYear Melbourne.
   * Half-open [start, endExclusive) covers through end of 30 June.
   */
  endExclusive: Date;
  /** Inclusive display end: 30 June endYear. */
  endInclusive: Date;
};

export type TimeRangeBounds = {
  /** Trailing window ending now. Omit for all-time or absolute FY / custom windows. */
  days?: number | null;
  /** Absolute window start (ISO Instant). Used by FY and custom presets. */
  startDate?: string;
  /** Absolute window end exclusive (ISO Instant). */
  endDate?: string;
};

const CUSTOM_RANGE_RE = /^custom\|(\d{4}-\d{2}-\d{2})\|(\d{4}-\d{2}-\d{2})$/;

function fyLabel(startYear: number, endYear: number): string {
  return `Financial Year ${startYear}–${String(endYear).slice(-2)}`;
}

/** Calendar Y/M/D in Australia/Melbourne for an Instant. */
function melbourneYmd(now: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: AU_FY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  const day = Number(parts.find((p) => p.type === 'day')?.value);
  return { year, month, day };
}

/**
 * Melbourne local midnight as a UTC Instant.
 * Uses the contemporaneous Melbourne offset (AEDT/AEST) via Intl.
 */
function melbourneLocalMidnightUtc(year: number, month: number, day: number): Date {
  const noonUtcGuess = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const offsetName = new Intl.DateTimeFormat('en-US', {
    timeZone: AU_FY_TIME_ZONE,
    timeZoneName: 'shortOffset',
  })
    .formatToParts(noonUtcGuess)
    .find((p) => p.type === 'timeZoneName')?.value;
  // e.g. "GMT+10", "GMT+11", "GMT+09:30"
  const match = offsetName?.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/i);
  const sign = match?.[1] === '-' ? -1 : 1;
  const hours = match?.[2] != null ? Number(match[2]) : 10;
  const minutes = match?.[3] != null ? Number(match[3]) : 0;
  const offsetMinutes = sign * (hours * 60 + minutes);
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - offsetMinutes * 60_000);
}

/** Start year of the AU FY containing `now` (FY starts 1 July Melbourne). */
export function currentAustralianFinancialYearStartYear(now: Date = new Date()): number {
  const { year, month } = melbourneYmd(now);
  return month >= 7 ? year : year - 1;
}

/** Australian financial year containing `now` — always 1 Jul → 30 Jun. */
export function currentAustralianFinancialYear(now: Date = new Date()): AustralianFinancialYear {
  return australianFinancialYearByOffset(0, now);
}

/** `offset` 0 = current FY; 1 = previous FY; … up to FINANCIAL_YEAR_HISTORY. */
export function australianFinancialYearByOffset(
  offset: number,
  now: Date = new Date(),
): AustralianFinancialYear {
  const safeOffset = Math.max(0, Math.floor(offset));
  const startYear = currentAustralianFinancialYearStartYear(now) - safeOffset;
  const endYear = startYear + 1;
  const start = melbourneLocalMidnightUtc(startYear, 7, 1);
  const endExclusive = melbourneLocalMidnightUtc(endYear, 7, 1);
  const endInclusive = melbourneLocalMidnightUtc(endYear, 6, 30);
  return {
    offset: safeOffset,
    startYear,
    endYear,
    label: fyLabel(startYear, endYear),
    value: `fy-${safeOffset as 0 | 1 | 2 | 3}`,
    start,
    endExclusive,
    endInclusive,
  };
}

export function listAustralianFinancialYears(
  history = FINANCIAL_YEAR_HISTORY,
  now: Date = new Date(),
): AustralianFinancialYear[] {
  return Array.from({ length: history + 1 }, (_, offset) =>
    australianFinancialYearByOffset(offset, now),
  );
}

/** Day / all-time / custom presets shared by Dashboard, Engagement, Finance. */
export function getAdminTimeRangeOptions(_now: Date = new Date()): readonly AdminTimeRangeOption[] {
  return [
    { value: 'all', label: 'All the time', days: null },
    { value: '1', label: 'Last 24 hours', days: 1 },
    { value: '7', label: 'Last 7 days', days: 7 },
    { value: '30', label: 'Last 30 days', days: 30 },
    { value: '60', label: 'Last 60 days', days: 60 },
    { value: '90', label: 'Last 90 days', days: 90 },
    { value: '180', label: 'Last 180 days', days: 180 },
    { value: '365', label: 'Last 365 days', days: 365 },
    { value: CUSTOM_TIME_RANGE_VALUE, label: 'Custom range', days: null },
  ];
}

export function isCustomTimeRange(value: string): boolean {
  return value === CUSTOM_TIME_RANGE_VALUE || CUSTOM_RANGE_RE.test(value);
}

/** True when the preset is lifetime “All the time” (no meaningful prior window). */
export function isAllTimeRange(value: string): boolean {
  return value === 'all';
}

/**
 * TM-3608: hide period-over-period % / “vs …” under All the time.
 * Other presets (including custom / FY with a finite window) keep comparison.
 */
export function shouldShowPriorComparison(rangeValue: string): boolean {
  return !isAllTimeRange(rangeValue);
}

export function parseCustomTimeRange(value: string): CustomTimeRangeParts | null {
  const match = CUSTOM_RANGE_RE.exec(value);
  if (!match?.[1] || !match[2]) return null;
  return { startYmd: match[1], endYmdInclusive: match[2] };
}

export function encodeCustomTimeRange(startYmd: string, endYmdInclusive: string): string {
  return `custom|${startYmd}|${endYmdInclusive}`;
}

export function parseYmd(ymd: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** Format a calendar YMD without shifting the day via the browser timezone. */
export function formatYmdLabel(ymd: string): string {
  const parts = parseYmd(ymd);
  if (!parts) return ymd;
  return format(new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0)), TIME_RANGE_DAY_FMT);
}

/** Melbourne half-open Instant window for an inclusive custom YMD range. */
export function customRangeToBounds(parts: CustomTimeRangeParts): TimeRangeBounds | null {
  const start = parseYmd(parts.startYmd);
  const end = parseYmd(parts.endYmdInclusive);
  if (!start || !end) return null;
  const startInstant = melbourneLocalMidnightUtc(start.year, start.month, start.day);
  // Day after inclusive end (UTC calendar arithmetic on Y/M/D).
  const next = new Date(Date.UTC(end.year, end.month - 1, end.day + 1));
  const endExclusive = melbourneLocalMidnightUtc(
    next.getUTCFullYear(),
    next.getUTCMonth() + 1,
    next.getUTCDate(),
  );
  if (!(endExclusive.getTime() > startInstant.getTime())) return null;
  return {
    startDate: toApiDateTime(startInstant),
    endDate: toApiDateTime(endExclusive),
  };
}

/**
 * TM-3568 multi-day trailing preset: last N Melbourne calendar days including today,
 * half-open [today−(N−1) midnight, tomorrow midnight).
 */
export function melbourneTrailingCalendarBounds(days: number, now: Date = new Date()): TimeRangeBounds {
  if (!Number.isFinite(days) || days < 2) {
    throw new Error('melbourneTrailingCalendarBounds requires days >= 2');
  }
  const { year, month, day } = melbourneYmd(now);
  // Tomorrow Melbourne (exclusive end).
  const endExclusiveUtc = new Date(Date.UTC(year, month - 1, day + 1));
  const endExclusive = melbourneLocalMidnightUtc(
    endExclusiveUtc.getUTCFullYear(),
    endExclusiveUtc.getUTCMonth() + 1,
    endExclusiveUtc.getUTCDate(),
  );
  // Start = endExclusive − days calendar days.
  const startUtc = new Date(Date.UTC(year, month - 1, day + 1 - days));
  const start = melbourneLocalMidnightUtc(
    startUtc.getUTCFullYear(),
    startUtc.getUTCMonth() + 1,
    startUtc.getUTCDate(),
  );
  return {
    startDate: toApiDateTime(start),
    endDate: toApiDateTime(endExclusive),
  };
}

/** Default seed when opening the custom calendar (last 30 Melbourne calendar days). */
export function defaultCustomRangeParts(now: Date = new Date()): CustomTimeRangeParts {
  const { year, month, day } = melbourneYmd(now);
  const endYmd = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const endUtc = new Date(Date.UTC(year, month - 1, day));
  const startUtc = subDays(endUtc, 29);
  const startYmd = `${startUtc.getUTCFullYear()}-${String(startUtc.getUTCMonth() + 1).padStart(2, '0')}-${String(startUtc.getUTCDate()).padStart(2, '0')}`;
  return { startYmd, endYmdInclusive: endYmd };
}

/** Menu highlight key: encoded custom values highlight the "Custom range" row. */
export function menuValueForTimeRange(value: string): string {
  return isCustomTimeRange(value) ? CUSTOM_TIME_RANGE_VALUE : value;
}

/**
 * Finance Management Time filter: shared day presets + current AU FY and prior 3 years.
 * Each FY is the full window 1 July → 30 June (query end exclusive = next 1 July).
 */
export function getFinanceTimeRangeOptions(now: Date = new Date()): readonly AdminTimeRangeOption[] {
  const fyOptions: AdminTimeRangeOption[] = listAustralianFinancialYears(
    FINANCIAL_YEAR_HISTORY,
    now,
  ).map((fy) => ({
    value: fy.value,
    label: fy.label,
    days: null,
  }));
  return [...getAdminTimeRangeOptions(now), ...fyOptions];
}

/** @deprecated Prefer getAdminTimeRangeOptions() / getFinanceTimeRangeOptions(). */
export const ADMIN_TIME_RANGE_OPTIONS = getAdminTimeRangeOptions();

const FY_VALUE_RE = /^fy-(\d+)$/;

export function parseFinancialYearOffset(value: string): number | null {
  const match = FY_VALUE_RE.exec(value);
  if (!match) return null;
  const offset = Number(match[1]);
  if (!Number.isInteger(offset) || offset < 0 || offset > FINANCIAL_YEAR_HISTORY) return null;
  return offset;
}

function toApiDateTime(date: Date): string {
  // Instant query params: keep Z so Spring binds java.time.Instant.
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** Resolve preset → API query bounds (trailing days and/or absolute ISO window). */
export function boundsForTimeRange(value: string, now: Date = new Date()): TimeRangeBounds {
  if (value === 'all') return {};
  const custom = parseCustomTimeRange(value);
  if (custom) {
    return customRangeToBounds(custom) ?? {};
  }
  // Bare "custom" is the menu row only — not an applied window.
  if (value === CUSTOM_TIME_RANGE_VALUE) return {};
  const fyOffset = parseFinancialYearOffset(value);
  if (fyOffset != null) {
    const fy = australianFinancialYearByOffset(fyOffset, now);
    return {
      startDate: toApiDateTime(fy.start),
      // Half-open end so 30 June is included.
      endDate: toApiDateTime(fy.endExclusive),
    };
  }
  const option = getAdminTimeRangeOptions(now).find((item) => item.value === value);
  if (option?.days === 1) return { days: 1 };
  if (option?.days != null && option.days > 1) {
    return melbourneTrailingCalendarBounds(option.days, now);
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed === 1) return { days: 1 };
  if (Number.isFinite(parsed) && parsed > 1) return melbourneTrailingCalendarBounds(parsed, now);
  return {};
}

/** Resolve preset value → trailing-window days, or `null` for all-time / absolute FY. */
export function daysForTimeRange(value: string, now: Date = new Date()): number | null {
  const bounds = boundsForTimeRange(value, now);
  if (bounds.startDate && bounds.endDate) {
    const start = parseISO(bounds.startDate);
    const end = parseISO(bounds.endDate);
    if (isValid(start) && isValid(end)) {
      return Math.max(1, differenceInCalendarDays(end, start));
    }
    return null;
  }
  return bounds.days ?? null;
}

export function labelForTimeRange(value: string, now: Date = new Date()): string {
  if (isCustomTimeRange(value)) return 'Custom range';
  const fyOffset = parseFinancialYearOffset(value);
  if (fyOffset != null) return australianFinancialYearByOffset(fyOffset, now).label;
  return getAdminTimeRangeOptions(now).find((item) => item.value === value)?.label ?? value;
}

/**
 * Closed-state subtitle for a selected Time filter value.
 * FY always shows "Jul 01, YYYY – Jun 30, YYYY+1".
 * Custom shows the inclusive Melbourne calendar days the user picked.
 * Multi-day trailing presets show inclusive Melbourne calendar days (today back N−1).
 */
export function formatTimeRangeDetailForValue(
  value: string,
  startDate?: string,
  endDate?: string,
  now: Date = new Date(),
): string {
  const custom = parseCustomTimeRange(value);
  if (custom) {
    return `${formatYmdLabel(custom.startYmd)} – ${formatYmdLabel(custom.endYmdInclusive)}`;
  }
  if (value === CUSTOM_TIME_RANGE_VALUE) {
    return 'Select start and end dates';
  }
  const fyOffset = parseFinancialYearOffset(value);
  if (fyOffset != null) {
    const fy = australianFinancialYearByOffset(fyOffset, now);
    return `${format(fy.start, TIME_RANGE_DAY_FMT)} – ${format(fy.endInclusive, TIME_RANGE_DAY_FMT)}`;
  }
  const days = daysForTimeRange(value, now);
  // Multi-day trailing presets: show inclusive Melbourne calendar days (not exclusive Instant end).
  if (days != null && days > 1) {
    const { year, month, day } = melbourneYmd(now);
    const endInclusive = formatYmdLabel(
      `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    );
    const startUtc = new Date(Date.UTC(year, month - 1, day + 1 - days));
    const startYmd = `${startUtc.getUTCFullYear()}-${String(startUtc.getUTCMonth() + 1).padStart(2, '0')}-${String(startUtc.getUTCDate()).padStart(2, '0')}`;
    return `${formatYmdLabel(startYmd)} – ${endInclusive}`;
  }
  // TM-3570 All the time: prefer API earliest→now bounds when present.
  if (value === 'all' && startDate && endDate) {
    return formatTimeRangeDetail(startDate, endDate, null);
  }
  return formatTimeRangeDetail(startDate, endDate, days);
}

function parseRangeDate(value: string): Date | null {
  const parsed = parseISO(value);
  if (isValid(parsed)) return parsed;
  const fallback = new Date(value);
  return isValid(fallback) ? fallback : null;
}

/**
 * Closed / open-trigger date subtitle for Time filters. Always includes the year.
 * Prefer {@link formatTimeRangeDetailForValue} when the selected preset is known
 * so Financial Year shows Jun 30 (inclusive) instead of the exclusive Jul 01 end.
 */
export function formatTimeRangeDetail(
  startDate?: string,
  endDate?: string,
  days?: number | null,
): string {
  if (startDate && endDate) {
    const start = parseRangeDate(startDate);
    const end = parseRangeDate(endDate);
    if (start && end) {
      return `${format(start, TIME_RANGE_DAY_FMT)} – ${format(end, TIME_RANGE_DAY_FMT)}`;
    }
  }
  if (days != null && Number.isFinite(days)) {
    const end = new Date();
    return `${format(subDays(end, days), TIME_RANGE_DAY_FMT)} – ${format(end, TIME_RANGE_DAY_FMT)}`;
  }
  return 'Choose a date range';
}
